import Sigma from "sigma";
import {Settings} from "sigma/settings";
import {Coordinates, NodeDisplayData, PartialButFor} from "sigma/types";
import {getPixelRatio} from "sigma/utils";
import {AppSettings} from "../settings";
import {Region} from "../types/Types";
import {darkenHexColor, hexToStringRgba, lightenHexColor} from "./colour";

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

export function resizeListener(sigma: Sigma, canvas: HTMLCanvasElement) {
  const {width, height} = sigma.getDimensions();
  const pixelRatio = getPixelRatio();
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Set new dimensions, this is identical to sigma's resize handling

  canvas.style.width = width + "px";
  canvas.style.height = height + "px";

  canvas.setAttribute("width", width * pixelRatio + "px");
  canvas.setAttribute("height", height * pixelRatio + "px");

  if (pixelRatio !== 1) ctx.scale(pixelRatio, pixelRatio);
}

export function clearCanvas(canvas: HTMLCanvasElement | null) {
  canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
}

export function buildSmoothQuadraticCurvePath(obj: CanvasRenderingContext2D | Path2D, vertices: Array<Coordinates>) {
  const lastVertex = vertices[vertices.length - 1];
  const firstVertex = vertices[0];
  const initialControlPointX = (lastVertex.x + firstVertex.x) / 2;
  const initialControlPointY = (lastVertex.y + firstVertex.y) / 2;
  obj.moveTo(initialControlPointX, initialControlPointY);

  for (let i = 0; i < vertices.length - 1; i++) {
    const currentVertex = vertices[i];
    const nextVertex = vertices[i + 1];
    const controlPointX = (currentVertex.x + nextVertex.x) / 2;
    const controlPointY = (currentVertex.y + nextVertex.y) / 2;
    obj.quadraticCurveTo(currentVertex.x, currentVertex.y, controlPointX, controlPointY);
  }

  const finalControlPointX = (firstVertex.x + lastVertex.x) / 2;
  const finalControlPointY = (firstVertex.y + lastVertex.y) / 2;
  obj.quadraticCurveTo(lastVertex.x, lastVertex.y, finalControlPointX, finalControlPointY);
  obj.closePath();
}

// Function to draw a quadratic curve path based on polygon vertices
export function drawSmoothQuadraticCurvePath(ctx: CanvasRenderingContext2D, vertices: Array<Coordinates>) {
  if (vertices.length < 3) return; // Need at least 3 points for a smooth curve

  ctx.beginPath();
  buildSmoothQuadraticCurvePath(ctx, vertices);

  // Set a default fill style if none is set
  if (!ctx.fillStyle || ctx.fillStyle === 'rgba(0, 0, 0, 0)') {
    ctx.fillStyle = "#000000";
  }

  ctx.fill();

  // Only stroke if strokeStyle is properly set
  if (ctx.strokeStyle && ctx.strokeStyle !== 'rgba(0, 0, 0, 0)') {
    ctx.stroke();
  }
}

export function drawRegions(
  sigma: Sigma,
  drawCtx: CanvasRenderingContext2D | null,
  regions: Array<Region> | null,
  textOnly: boolean
): void {
  if (!drawCtx || !regions) return;
  drawCtx.globalCompositeOperation = 'source-over';

  if (!textOnly) {
    regions.forEach((value) => {
      drawRegion(sigma, drawCtx, value);
      drawRegionText(sigma, drawCtx, value);
    });
  }
}

export function drawRegion(
  sigma: Sigma,
  drawCtx: CanvasRenderingContext2D,
  region: Region
) {
  // const zoom = sigma.getCamera().ratio;
  const coordinates = sigma.graphToViewport(region as Coordinates);  // Convert x,y to on-screen coord
  const zero = sigma.graphToViewport({x: 0, y: 0}).x;
  const r = (sigma.graphToViewport({x: region.r, y: 0}).x - zero) * 1.5;

  const {x, y} = coordinates;

  const grd = drawCtx.createRadialGradient(
    x, y, r * 0.06,
    x, y, r * 0.9
  );
  // Stronger inner, subtle mid halo, still soft outer falloff
  grd.addColorStop(0.0, hexToStringRgba(region.colour as string, 0.65));
  grd.addColorStop(1.0, hexToStringRgba(region.colour as string, 0.00));

  drawCtx.fillStyle = grd;
  drawCtx.beginPath();
  drawCtx.arc(x, y, r * 1.2, 0, 2 * Math.PI);
  drawCtx.fill();
  drawCtx.closePath();
}

export function drawRegionText(
  sigma: Sigma,
  textCtx: CanvasRenderingContext2D,
  region: Region,
): void {
  const zoom = sigma.getCamera().ratio ** .7;  // scale font size with zoom ratio
  const {x, y} = sigma.graphToViewport({x: region.label_x, y: region.label_y});
  const text_height = 16 / zoom;  // our font size base is 16px

  // Text of the region
  textCtx.font = `bold ${text_height}px Arial`;
  textCtx.textBaseline = 'middle';
  textCtx.lineWidth = .4 / zoom;

  textCtx.fillStyle = region.colour as string;
  textCtx.strokeStyle = lightenHexColor(region.colour as string, .1); // Lighten color

  let offset = 0
  region.label.split("\n").forEach(part => {
    textCtx.fillText(part, x, y + (offset * text_height));
    textCtx.strokeText(part, x, y + (offset * text_height));
    offset++;
  });
}

export function drawHover(
  context: CanvasRenderingContext2D,
  data: PartialButFor<NodeDisplayData, "x" | "y" | "size" | "label" | "color">,
  settings: Settings
) {
  const size = settings.labelSize, font = settings.labelFont, weight = settings.labelWeight;
  context.font = `${weight} ${size}px ${font}`;

  context.fillStyle = darkenHexColor(data.color, .5);
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 5;
  context.shadowColor = "#FFF";

  context.beginPath();
  context.arc(data.x, data.y, data.size + 2, 0, Math.PI * 2);
  context.closePath();
  context.fill();

  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 0;

  if (!data.legendHighlighted) {
    drawLabel(context, data, settings);
  }
}

/**
 * Custom label renderer
 */
export function drawLabel(
  context: CanvasRenderingContext2D,
  data: PartialButFor<NodeDisplayData, "x" | "y" | "size" | "label" | "color">,
  settings: Settings,
): void {
  if (!data.label) return;

  // IMPORTANT: set font size BEFORE setting the background style to match
  // the font size of the text

  // Set the font size, weight, and font family from data or settings
  const size = data.selected ? ((data as any).labelSize || settings.labelSize) + 6 : (data as any).labelSize || settings.labelSize;
  const weight = (data as any).labelWeight || settings.labelWeight;
  const font = (data as any).labelFont || settings.labelFont;
  const label = data.label;
  const x = data.x + data.size + 3;
  const y = data.y + size / 3;
  const padding = 4;
  const borderRadius = 5;

  context.save();
  context.font = `${weight} ${size}px ${font}`;

  // Measure text
  const metrics = context.measureText(label);
  const textWidth = metrics.width;
  const ascent = metrics.actualBoundingBoxAscent ?? size * 0.8;
  const descent = metrics.actualBoundingBoxDescent ?? size * 0.2;
  const textHeight = ascent + descent;
  const boxX = x - padding;
  const boxY = y - ascent - padding;
  const boxWidth = textWidth + padding * 2;
  const boxHeight = textHeight + padding * 2;

  // Draw background with rounded corners
  context.beginPath();
  roundRect(context, boxX, boxY, boxWidth, boxHeight, borderRadius);
  context.fillStyle = AppSettings.TEXT_BACKGROUND_COLOUR;
  context.fill();
  context.strokeStyle = data.selected ? data.color : AppSettings.TEXT_BORDER_COLOUR;
  context.lineWidth = data.selected ? 2 : 1;
  context.stroke();

  // Set the text properties
  context.fillStyle = AppSettings.TEXT_COLOUR;
  context.fillText(label, x, y);

  context.restore();
}
